import _ from "lodash";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useMemo } from 'react';
import { usePropertyAiGradeStore, usePropertyStore } from '../..';
import IProperty from "../../interfaces/IProperty";
import { IBase64ScreenshotSet } from "../../services/propertyAiGradeApiService";
import { areScreenshotsSet } from "../../utils/aiGradeUtils";
import MapThumbnail from "../MapThumbnail";
import SatelliteArcGISBaseMapLayer from "../baseMapLayers/SatelliteArcGISBaseMapLayer";
import SatelliteBingBaseMapLayer from "../baseMapLayers/SatelliteBingBaseMapLayer";
import SatelliteGoogleBaseMapLayer from "../baseMapLayers/SatelliteGoogleBaseMapLayer";
import SatelliteMapboxBaseMapLayer from '../baseMapLayers/SatelliteMapboxBaseMapLayer';

function AiGradeScreenshotsCollector() {
    const aiGradeStore = usePropertyAiGradeStore();
    const propertyStore = usePropertyStore();

    const propertiesNeedingScreenshots = useMemo(() => computed(() =>
        _.uniqBy(Array.from(propertyStore.properties.values()), p => p.id)
            .filter((property) =>
                aiGradeStore.isLoadingFor[property.id]
                && aiGradeStore.screenshotsFor[property.id]
                && !areScreenshotsSet(aiGradeStore.screenshotsFor[property.id])
            )
    ), []).get();

    if (!propertiesNeedingScreenshots.length) return null;

    return (
        <>
            {propertiesNeedingScreenshots.map((property) => (
                <PropertyScreenshotsCollector key={property.id} property={property} />
            ))}
        </>
    );
}

export default observer(AiGradeScreenshotsCollector);

const PropertyScreenshotsCollector = observer(({ property }: { property: IProperty }) => {
    const aiGradeStore = usePropertyAiGradeStore();

    const setScreenshot = (key: keyof IBase64ScreenshotSet, b64: string) => {
        aiGradeStore.setScreenshotForProperty(property.id, key, b64);
    }

    return (
        <>
            <MapThumbnail id={`satelliteMapboxMap_${property.id}`} shapes={property.shapes} compressScreenshot onBase64ScreenshotIsReady={(b64) => setScreenshot("mapbox", b64)}>
                {(isStyleLoaded) => <SatelliteMapboxBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />}
            </MapThumbnail>
            <MapThumbnail id={`satelliteGoogleMap_${property.id}`} shapes={property.shapes} compressScreenshot onBase64ScreenshotIsReady={(b64) => setScreenshot("google", b64)}>
                {(isStyleLoaded) => <SatelliteGoogleBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />}
            </MapThumbnail>
            <MapThumbnail id={`satelliteArcGisMap_${property.id}`} shapes={property.shapes} compressScreenshot onBase64ScreenshotIsReady={(b64) => setScreenshot("arcGis", b64)}>
                {(isStyleLoaded) => <SatelliteArcGISBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />}
            </MapThumbnail>
            <MapThumbnail id={`satelliteBingMap_${property.id}`} shapes={property.shapes} compressScreenshot onBase64ScreenshotIsReady={(b64) => setScreenshot("bing", b64)}>
                {(isStyleLoaded) => <SatelliteBingBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />}
            </MapThumbnail>
        </>
    );
});
